import React from "react";
import {
  makeStyles,
  Box,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  Table,
  IconButton,
  TablePagination
} from "@material-ui/core";
import { remCalc, getDateParams, formatCurrency } from "../../../utils/helpers";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import Skeleton from "@material-ui/lab/Skeleton";
import clsx from "clsx";
import OptimizedTooltip from "../../common/OptimizedTooltip";

const useStyles = makeStyles(theme => ({
  table: {
    width: remCalc(448),
    marginLeft: theme.spacing(-3),
    "& .MuiTableCell-root": {
      "&:first-child": {
        paddingLeft: theme.spacing(4)
      },
      "&:last-child": {
        paddingRight: theme.spacing(4)
      },
      lineHeight: "1rem"
    }
  },
  tableHead: {
    backgroundColor: "#F7F7F7",
    "& th": {
      color: "#808080",
      fontSize: "0.75rem",
      borderBottomWidth: 1,
      borderBottomColor: "#EEEEEE"
    },
    "& tr": {
      height: remCalc(50)
    }
  },
  tableRow: {
    height: remCalc(50),
    cursor: "pointer",
    "& .view-invoice-icon": {
      display: "none",
      margin: theme.spacing(-1.5, 0, -1.5, -1),
      "&:hover": {
        backgroundColor: "#F7F7F7"
      }
    },
    "&:hover": {
      "& .status-text": {
        display: "none"
      },
      "& .view-invoice-icon": {
        display: "inline-flex"
      },
      backgroundColor: "#EEEEEE"
    }
  },
  draft: {
    "&:hover": {
      "& .status-text": {
        display: "inline-flex"
      },
      "& .view-invoice-icon": {
        display: "none"
      }
    }
  },
  pagination: {
    width: remCalc(448),
    marginLeft: theme.spacing(-3),
    boxSizing: "border-box",
    paddingRight: theme.spacing(4) + "px !important",
    borderBottom: "1px solid #eee",
    "& .Mui-disabled .MuiSvgIcon-root": {
      color: " rgba(0, 0, 0, 0.26)"
    },
    "& .MuiTablePagination-caption": {
      fontSize: "0.9375rem"
    }
  }
}));

function Invoices({ invoices, invoicesLoaded }) {
  const classes = useStyles();

  const [page, setPage] = React.useState(0);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Box
      pt={4}
      mr={-2}
      display="flex"
      flexDirection="column"
      height="100%"
      style={{ boxSizing: "border-box" }}
    >
      <Table className={classes.table} aria-label="simple table">
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell>Invoice #</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Total</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!invoices.length && !invoicesLoaded ? (
            <TableRow className={classes.tableRow}>
              <TableCell colSpan="4" align="center">
                <Skeleton height={28} />
                <Skeleton height={28} />
                <Skeleton height={28} />
              </TableCell>
            </TableRow>
          ) : !invoices.length ? (
            <TableRow className={classes.tableRow}>
              <TableCell colSpan="4" align="center">
                No data available
              </TableCell>
            </TableRow>
          ) : (
            invoices.slice(page * 10, page * 10 + 10).map(row => {
              const date = getDateParams(row.created * 1000);
              const total = formatCurrency(row.amountPaid, row.currency);
              return (
                <TableRow
                  key={row.id}
                  className={clsx(classes.tableRow, {
                    [classes.draft]: row.status && row.status === "draft"
                  })}
                >
                  <TableCell>{row.number}</TableCell>
                  <TableCell>{`${date.day} ${date.month} ${date.year}`}</TableCell>
                  <TableCell>{total}</TableCell>
                  <TableCell>
                    <span className="status-text">
                      {row.status ? row.status.toUpperCase() : "N/A"}
                    </span>
                    <OptimizedTooltip placement="bottom" title="View invoice">
                      <IconButton
                        component="a"
                        href={`${row.hostedInvoiceUrl}`}
                        target="_blank"
                        className={"view-invoice-icon"}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </OptimizedTooltip>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {!!invoices.length && (
        <TablePagination
          className={classes.pagination}
          component="div"
          count={invoices.length}
          rowsPerPageOptions={[10]}
          rowsPerPage={10}
          page={page}
          onChangePage={handleChangePage}
        />
      )}
    </Box>
  );
}

export default Invoices;
